import {
  useAllAgitems,
  useAgitemDetailsWithPricelist,
} from "http-hooks/agitem/useAgitem";

import { useAllShapes } from "http-hooks/shape/useShape";
import { useAllColors } from "http-hooks/color/useColor";
import { useCreatePricelistDetail } from "http-hooks/pricelistdetail/usePricelistDetail";
import { useState, useEffect, useMemo, useCallback, memo } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import Select from "components/ui/Select";
import Input from "components/ui/Input";
import {
  SelectPriceLists,
  SelectBrandAndSubbrands,
} from "components/ui/selectors";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  LoadingRow,
  EmptyRow,
} from "components/ui/Table";

const typeOptions = [
  { value: "B", label: "Brandwise" },
  { value: "I", label: "Itemwise" },
];

const sizeTypeOptions = [
  { value: "", label: "All" },
  { value: "B", label: "Big" },
  { value: "S", label: "Small" },
];

const rateFilterOptions = [
  { value: "all", label: "All" },
  { value: "exclude_zero", label: "Exclude 0" },
  { value: "zero_only", label: "Zero Only" },
];

// Memoized flat table row component
const PricelistTableRow = memo(
  ({ agitem, rateItem, onItemValueChange, rateFieldName }) => {
    return (
      <TR key={agitem.agitemdtlId || agitem.itemId}>
        <TD first style={{ minWidth: "300px" }}>
          {agitem.fullItemName || "N/A"}
        </TD>
        <TD style={{ minWidth: "120px" }}>
          <Input
            type="number"
            value={rateItem?.plamt || ""}
            onChange={(e) => onItemValueChange(agitem.agitemdtlId, e.target)}
            name="plamt"
            showLabel={false}
          />
        </TD>
        <TD style={{ minWidth: "100px" }}>
          <Input
            type="number"
            value={rateItem?.dis1 || ""}
            onChange={(e) => onItemValueChange(agitem.agitemdtlId, e.target)}
            name="dis1"
            showLabel={false}
          />
        </TD>
        <TD style={{ minWidth: "100px" }}>
          <Input
            type="number"
            value={rateItem?.dis2 || ""}
            onChange={(e) => onItemValueChange(agitem.agitemdtlId, e.target)}
            name="dis2"
            showLabel={false}
          />
        </TD>
        <TD style={{ minWidth: "100px" }}>
          <Input
            type="number"
            value={rateItem?.dis3 || ""}
            onChange={(e) => onItemValueChange(agitem.agitemdtlId, e.target)}
            name="dis3"
            showLabel={false}
          />
        </TD>
        <TD style={{ minWidth: "100px" }}>
          <Input
            type="number"
            value={rateItem?.dis4 || ""}
            onChange={(e) => onItemValueChange(agitem.agitemdtlId, e.target)}
            name="dis4"
            showLabel={false}
          />
        </TD>
        <TD style={{ minWidth: "100px" }}>
          <Input
            type="number"
            value={rateItem?.dis5 || ""}
            onChange={(e) => onItemValueChange(agitem.agitemdtlId, e.target)}
            name="dis5"
            showLabel={false}
          />
        </TD>
        <TD last style={{ minWidth: "120px" }}>
          <Input
            type="number"
            value={rateItem?.[rateFieldName] || ""}
            name={rateFieldName}
            showLabel={false}
            isDisabled={true}
            className="bg-gray-100"
          />
        </TD>
      </TR>
    );
  }
);

PricelistTableRow.displayName = "PricelistTableRow";

// Memoized grouped table row — one row per size group (or ungrouped item)
const PricelistGroupRow = memo(
  ({ groupLabel, sizes, szuom, itemLabel, rateItem, onGroupValueChange, rateFieldName }) => {
    return (
      <TR>
        <TD first style={{ minWidth: "300px" }}>
          <div className="font-medium text-gray-900">{itemLabel}</div>
          <div className="text-xs text-indigo-600 mt-0.5">{sizes}{szuom ? ` ${szuom}` : ""}</div>
        </TD>
        <TD style={{ minWidth: "120px" }}>
          <Input
            type="number"
            value={rateItem?.plamt || ""}
            onChange={(e) => onGroupValueChange(groupLabel, e.target)}
            name="plamt"
            showLabel={false}
          />
        </TD>
        <TD style={{ minWidth: "100px" }}>
          <Input
            type="number"
            value={rateItem?.dis1 || ""}
            onChange={(e) => onGroupValueChange(groupLabel, e.target)}
            name="dis1"
            showLabel={false}
          />
        </TD>
        <TD style={{ minWidth: "100px" }}>
          <Input
            type="number"
            value={rateItem?.dis2 || ""}
            onChange={(e) => onGroupValueChange(groupLabel, e.target)}
            name="dis2"
            showLabel={false}
          />
        </TD>
        <TD style={{ minWidth: "100px" }}>
          <Input
            type="number"
            value={rateItem?.dis3 || ""}
            onChange={(e) => onGroupValueChange(groupLabel, e.target)}
            name="dis3"
            showLabel={false}
          />
        </TD>
        <TD style={{ minWidth: "100px" }}>
          <Input
            type="number"
            value={rateItem?.dis4 || ""}
            onChange={(e) => onGroupValueChange(groupLabel, e.target)}
            name="dis4"
            showLabel={false}
          />
        </TD>
        <TD style={{ minWidth: "100px" }}>
          <Input
            type="number"
            value={rateItem?.dis5 || ""}
            onChange={(e) => onGroupValueChange(groupLabel, e.target)}
            name="dis5"
            showLabel={false}
          />
        </TD>
        <TD last style={{ minWidth: "120px" }}>
          <Input
            type="number"
            value={rateItem?.[rateFieldName] || ""}
            name={rateFieldName}
            showLabel={false}
            isDisabled={true}
            className="bg-gray-100"
          />
        </TD>
      </TR>
    );
  }
);

PricelistGroupRow.displayName = "PricelistGroupRow";

export default function PricelistDetail() {
  const [inputChangeRupee, setInputChangeRupee] = useState(0);
  const [inputChangePercent, setInputChangePercent] = useState(0);
  const [searchShape, setSearchShape] = useState("");
  const [searchColor, setSearchColor] = useState("");
  const [searchSizeType, setSearchSizeType] = useState("");
  const [billingRateFilter, setBillingRateFilter] = useState("all");
  const [rateList, setRateList] = useState(null);
  const [selectedPricelist, setSelectedPricelist] = useState(null);
  const [selectedBrands, setSelectedBrands] = useState(null);
  const [selectedItems, setSelectedItems] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [viewMode, setViewMode] = useState("flat"); // "flat" | "group"

  const { mutateAsync: mutateCreate, isPending: isSavingPricelistDetail } =
    useCreatePricelistDetail();

  // Fetch agitems for Itemwise dropdown options
  const { isLoading: isAllAgitemsLoading, data: { data: allagitems } = [] } =
    useAllAgitems(
      {
        itmvendorId: selectedPricelist?.supid,
        ag_status: 1,
      },
      !!(selectedPricelist && selectedType?.value === "I")
    );

  // Unified hook to fetch item details with pricelist for both Brandwise and Itemwise
  const selectedItemIds = Array.isArray(selectedItems)
    ? selectedItems.map((item) => item.value)
    : [];
  const selectedBrandIds = selectedBrands ? [selectedBrands.value] : [];

  const {
    isLoading: isItemDetailsLoading,
    data: itemDetailsWithPricelist = [],
  } = useAgitemDetailsWithPricelist(
    {
      ...(selectedType?.value === "I" && {
        itemIds: selectedItemIds.join(","),
      }),
      ...(selectedType?.value === "B" && {
        brandIds: selectedBrandIds.join(","),
      }),
      ...(selectedPricelist?.rateid && {
        pricelistId: selectedPricelist.rateid,
        includePricelistDetail: true,
      }),
    },
    !!(
      ((Array.isArray(selectedItems) &&
        selectedItems.length > 0 &&
        selectedType?.value === "I") ||
        (selectedBrands && selectedType?.value === "B")) &&
      selectedPricelist
    )
  );

  // Fetch shapes and colors directly
  const { isLoading: isAllShapesLoading, data: { data: allshapes } = [] } =
    useAllShapes();
  const { isLoading: isAllColorsLoading, data: { data: allcolors } = [] } =
    useAllColors();

  // Memoized filtered items based on shape, color, and size type filters
  // Helper function to calculate billing rate or sale rate based on pricelist type
  const calculateRate = (item) => {
    const plamt = parseFloat(item.plamt) || 0;
    const dis1 = parseFloat(item.dis1) || 0;
    const dis2 = parseFloat(item.dis2) || 0;
    const dis3 = parseFloat(item.dis3) || 0;
    const dis4 = parseFloat(item.dis4) || 0;
    const dis5 = parseFloat(item.dis5) || 0;

    return plamt - dis1 - dis2 - dis3 - dis4 - dis5;
  };

  // Check if current pricelist is Sales type
  const isSalesType = selectedPricelist?.typ === 'S';
  const rateColumnLabel = isSalesType ? "Sale Rate" : "Billing Rate";
  const rateFieldName = isSalesType ? "salerate" : "billingrate";

  const filteredItemDetails = useMemo(() => {
    if (!itemDetailsWithPricelist || itemDetailsWithPricelist.length === 0)
      return null;

    return itemDetailsWithPricelist.filter((item) => {
      // Get the corresponding rate from rateList using agitemdtlId
      const rateItem = rateList?.find((rateItem) => rateItem.itemdid === item.agitemdtlId);
      const rate = rateItem ? parseFloat(rateItem[rateFieldName]) || 0 : 0;

      // Apply rate filter
      let rateMatch = true;
      if (billingRateFilter === "exclude_zero") {
        rateMatch = rate !== 0;
      } else if (billingRateFilter === "zero_only") {
        rateMatch = rate === 0;
      }

      return (
        (searchShape === "" || item.shp === searchShape) &&
        (searchColor === "" || item.clt === searchColor) &&
        (searchSizeType === "" || item.itmsztyp === searchSizeType) &&
        rateMatch
      );
    });
  }, [
    itemDetailsWithPricelist,
    searchShape,
    searchColor,
    searchSizeType,
    billingRateFilter,
    rateList,
    rateFieldName,
  ]);

  // Custom onChange handlers with reset logic
  const handlePricelistChange = useCallback((newPricelist) => {
    setSelectedPricelist(newPricelist);
    // Reset all dependent states when pricelist changes
    setRateList(null);
    setSelectedType(null);
    setSelectedBrands(null);
    setSelectedItems(null);
    setInputChangeRupee(0);
  }, []);

  const handleTypeChange = useCallback((newType) => {
    setSelectedType(newType);
    // Reset dependent states when type changes
    setSelectedBrands(null);
    setSelectedItems(null);
    setRateList(null);
    setInputChangeRupee(0);
  }, []);

  const handleBrandsChange = useCallback((newBrands) => {
    setSelectedBrands(newBrands);
    // Reset rate list when brands change
    setRateList(null);
  }, []);

  // Handle both Brandwise and Itemwise selection using unified API
  useEffect(() => {
    if (
      itemDetailsWithPricelist &&
      itemDetailsWithPricelist.length > 0 &&
      ((selectedType?.value === "B" && selectedBrands?.value) ||
        (selectedType?.value === "I" &&
          Array.isArray(selectedItems) &&
          selectedItems.length > 0))
    ) {
      // Create rate list array from fetched item details
      const newArray = itemDetailsWithPricelist.map((item) => {
        const rateItem = {
          itemdid: item.agitemdtlId, // Use agitemdtlId for pricelist detail
          rateid: selectedPricelist?.rateid,
          ratedt: selectedPricelist?.effrctivedt,
          plamt: parseFloat(item.pricelistdetails?.[0]?.plamt) || 0,
          dis1: parseFloat(item.pricelistdetails?.[0]?.dis1) || 0,
          dis2: parseFloat(item.pricelistdetails?.[0]?.dis2) || 0,
          dis3: parseFloat(item.pricelistdetails?.[0]?.dis3) || 0,
          dis4: parseFloat(item.pricelistdetails?.[0]?.dis4) || 0,
          dis5: parseFloat(item.pricelistdetails?.[0]?.dis5) || 0,
          billingrate: 0, // Initialize both fields
          salerate: 0,
        };

        // Calculate rate using the helper function
        rateItem[rateFieldName] = calculateRate(rateItem);

        return rateItem;
      });

      setRateList(newArray);
    }
  }, [
    itemDetailsWithPricelist,
    selectedPricelist,
    selectedType,
    selectedBrands,
    selectedItems,
    rateFieldName,
  ]);

  const applyRupeeChange = useCallback(() => {
    if (!rateList || inputChangeRupee === 0) return;

    const changeAmount = parseFloat(inputChangeRupee) || 0;
    const newList = rateList.map((item) => {
      const updatedItem = { ...item };
      const currentAmount = parseFloat(updatedItem.plamt) || 0;
      updatedItem.plamt = currentAmount + changeAmount;
      updatedItem[rateFieldName] = calculateRate(updatedItem);
      return updatedItem;
    });

    setRateList(newList);
  }, [rateList, inputChangeRupee, rateFieldName]);

  const applyPercentChange = useCallback(() => {
    if (!rateList || inputChangePercent === 0) return;

    const changePercent = parseFloat(inputChangePercent) || 0;
    const newList = rateList.map((item) => {
      const updatedItem = { ...item };
      const currentAmount = parseFloat(updatedItem.plamt) || 0;
      const changeAmount = (currentAmount * changePercent) / 100;
      updatedItem.plamt = currentAmount + changeAmount;
      updatedItem[rateFieldName] = calculateRate(updatedItem);
      return updatedItem;
    });

    setRateList(newList);
  }, [rateList, inputChangePercent, rateFieldName]);

  const setItemValues = useCallback(
    (itemdid, target) => {
      const currentList = rateList || [];
      if (!currentList) return;

      // Find the specific item index to update
      const itemIndex = currentList.findIndex(
        (item) => item.itemdid === itemdid
      );
      if (itemIndex === -1) return;

      // Create a new array with only the changed item updated
      const newList = [...currentList];
      const updatedItem = { ...newList[itemIndex] };
      updatedItem[target.name] = parseFloat(target.value) || 0;
      updatedItem[rateFieldName] = calculateRate(updatedItem);
      newList[itemIndex] = updatedItem;

      setRateList(newList);
    },
    [rateList, rateFieldName]
  );

  // Update all items in a size group at once (group view)
  const setGroupValues = useCallback(
    (groupKey, target) => {
      if (!rateList) return;
      const newList = rateList.map((item) => {
        const agitem = filteredItemDetails?.find((a) => a.agitemdtlId === item.itemdid);
        const itemGroupKey = agitem?.size_grouping_id
          ? `${agitem.size_grouping_id}__${agitem.shp}__${agitem.clt}__${agitem.agitemdtlId.substring(0, 10)}`
          : `ungrouped__${item.itemdid}`;
        if (itemGroupKey !== groupKey) return item;
        const updatedItem = { ...item };
        updatedItem[target.name] = parseFloat(target.value) || 0;
        updatedItem[rateFieldName] = calculateRate(updatedItem);
        return updatedItem;
      });
      setRateList(newList);
    },
    [rateList, filteredItemDetails, rateFieldName]
  );

  // Build grouped rows for group view mode
  const groupedRows = useMemo(() => {
    if (!filteredItemDetails || !rateList) return [];
    const seen = new Map();
    const result = [];

    for (const agitem of filteredItemDetails) {
      const rateItem = rateList.find((r) => r.itemdid === agitem.agitemdtlId);
      const groupId = agitem.size_grouping_id;
      const itemPrefix = agitem.agitemdtlId.substring(0, 10);

      if (groupId) {
        // Group key: same size_grouping_id + same item (prefix) + same shape + same color
        const groupKey = `${groupId}__${agitem.shp}__${agitem.clt}__${itemPrefix}`;
        if (!seen.has(groupKey)) {
          // Collect all sizes for this group from filteredItemDetails
          const groupItems = filteredItemDetails.filter(
            (a) =>
              a.size_grouping_id === groupId &&
              a.agitemdtlId.substring(0, 10) === itemPrefix &&
              a.shp === agitem.shp &&
              a.clt === agitem.clt
          );
          const sizes = groupItems.map((a) => a.sz).join(", ");
          seen.set(groupKey, true);
          result.push({
            type: "group",
            groupKey,
            itemLabel: agitem.fullItemName
              ? agitem.fullItemName.replace(/\s+\S+$/, "").trim() // strip last token (size)
              : agitem.agitemdtlId.substring(0, 10),
            sizes,
            szuom: agitem.szuom,
            rateItem,
          });
        }
      } else {
        result.push({
          type: "flat",
          groupKey: `ungrouped__${agitem.agitemdtlId}`,
          agitem,
          rateItem,
        });
      }
    }

    return result;
  }, [filteredItemDetails, rateList]);

  // Item options for Itemwise type
  const itemOptions = useMemo(() => {
    if (!allagitems || !selectedPricelist) return [];

    return allagitems.map((item) => ({
      label:
        (item.brand ? item.brand + " " : "") +
        (item.subbrandname ? item.subbrandname + " " : "") +
        (item.itemCode ? item.itemCode + " " : "") +
        (item.itemName ? item.itemName : ""),
      value: item.itemId,
    }));
  }, [allagitems, selectedPricelist]);

  // Shape options from API
  const shapeOptions = useMemo(() => {
    return [
      { value: "", label: "All" },
      ...(allshapes?.map((shape) => ({
        value: shape.shapetype, // Use shapetype (name) instead of shapeId
        label: shape.shapetype,
      })) || []),
    ];
  }, [allshapes]);

  // Color options from API
  const colorOptions = useMemo(() => {
    return [
      { value: "", label: "All" },
      ...(allcolors?.map((color) => ({
        value: color.clrname, // Use clrname (name) instead of clrId
        label: color.clrname,
      })) || []),
    ];
  }, [allcolors]);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="font-semibold leading-6 text-3xl text-gray-900">
            Pricelist Detail
          </h1>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              onClick={() => setViewMode("flat")}
              className={`px-4 py-2 text-sm font-semibold rounded-l-md border ${
                viewMode === "flat"
                  ? "bg-gray-700 text-white border-gray-700"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              Flat
            </button>
            <button
              type="button"
              onClick={() => setViewMode("group")}
              className={`px-4 py-2 text-sm font-semibold rounded-r-md border-t border-b border-r ${
                viewMode === "group"
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              Group
            </button>
          </div>
        </div>
      </div>

      {/* First row - Pricelist and Type selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 mt-4">
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <SelectPriceLists
            label="Vendor & Effective Date"
            value={selectedPricelist}
            onChange={handlePricelistChange}
            placeholder="Select pricelist"
            filters={{ ag_status: 1 }}
          />
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm">
          <Select
            label="Type"
            value={selectedType}
            onChange={handleTypeChange}
            options={typeOptions}
            placeholder="Select type"
            isClearable={true}
          />
        </div>
      </div>

      {/* Second row - Brand or Item selection based on type */}
      <div className="bg-white rounded-lg p-4 mt-4 shadow-sm">
        {selectedType?.value === "B" ? (
          <SelectBrandAndSubbrands
            label="Brand/Sub-brand Name"
            value={selectedBrands}
            onChange={handleBrandsChange}
            placeholder="Select brand or sub-brand"
            isMulti={false}
            isClearable={true}
            isDisabled={!selectedPricelist || !selectedType}
            filters={{ ag_status: "1" }}
            vendorId={selectedPricelist?.supid}
          />
        ) : selectedType?.value === "I" ? (
          <Select
            label="Item Names"
            value={selectedItems}
            onChange={(newItems) => {
              setSelectedItems(newItems);
              setRateList(null); // Reset rate list when items change
            }}
            options={itemOptions}
            placeholder="Select items"
            isMulti={true}
            isSearchable={true}
            isClearable={true}
            isDisabled={!selectedPricelist || !selectedType}
            isLoading={isAllAgitemsLoading}
          />
        ) : (
          <Select
            label="Selection"
            value={null}
            onChange={() => {}}
            options={[]}
            placeholder="Select type first"
            isDisabled={true}
          />
        )}
      </div>

      {rateList !== "" &&
        (selectedBrands ||
          (Array.isArray(selectedItems) && selectedItems.length > 0)) && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4 mt-4">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <Select
                label="Shape"
                value={
                  shapeOptions.find((option) => option.value === searchShape) ||
                  null
                }
                onChange={(selected) => setSearchShape(selected?.value || "")}
                options={shapeOptions}
                placeholder="Select shape"
                isClearable={true}
                isLoading={isAllShapesLoading}
              />
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm">
              <Select
                label="Color"
                value={
                  colorOptions.find((option) => option.value === searchColor) ||
                  null
                }
                onChange={(selected) => setSearchColor(selected?.value || "")}
                options={colorOptions}
                placeholder="Select color"
                isClearable={true}
                isLoading={isAllColorsLoading}
              />
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm">
              <Select
                label="Size Type"
                value={
                  sizeTypeOptions.find(
                    (option) => option.value === searchSizeType
                  ) || null
                }
                onChange={(selected) =>
                  setSearchSizeType(selected?.value || "")
                }
                options={sizeTypeOptions}
                placeholder="Select size type"
                isClearable={true}
              />
            </div>
          </div>
        )}

      {rateList !== "" &&
        (selectedBrands ||
          (Array.isArray(selectedItems) && selectedItems.length > 0)) && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4 mt-4">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <label
                htmlFor="changeinrupee"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Change Rs.(+/-)
              </label>
              <div className="mt-2 flex rounded-md shadow-sm">
                <div className="relative flex flex-grow items-stretch focus-within:z-10">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <MagnifyingGlassIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </div>
                  <Input
                    value={inputChangeRupee}
                    name="changeinrupee"
                    type="number"
                    className="block w-full rounded-l-md border-0 py-2 pl-10 pr-2 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Change in rupee"
                    onChange={(e) => setInputChangeRupee(e.target.value)}
                    showLabel={false}
                  />
                  <button
                    type="button"
                    onClick={applyRupeeChange}
                    className="relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm">
              <label
                htmlFor="changeinpercent"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Change %(+/-)
              </label>
              <div className="mt-2 flex rounded-md shadow-sm">
                <div className="relative flex flex-grow items-stretch focus-within:z-10">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <MagnifyingGlassIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </div>
                  <Input
                    value={inputChangePercent}
                    name="changeinpercent"
                    type="number"
                    className="block w-full rounded-l-md border-0 py-2 pl-10 pr-2 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Change in percent"
                    onChange={(e) => setInputChangePercent(e.target.value)}
                    showLabel={false}
                  />
                  <button
                    type="button"
                    onClick={applyPercentChange}
                    className="relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm">
              <Select
                label={`${rateColumnLabel} Filter`}
                value={
                  rateFilterOptions.find(
                    (option) => option.value === billingRateFilter
                  ) || null
                }
                onChange={(selected) =>
                  setBillingRateFilter(selected?.value || "all")
                }
                options={rateFilterOptions}
                placeholder="Select filter"
                isClearable={false}
              />
            </div>
          </div>
        )}

      <div className="mt-8">
        {filteredItemDetails && filteredItemDetails.length > 0 && (
          <>
            <div className="mb-4 text-sm text-gray-600">
              {viewMode === "group"
                ? `Showing ${groupedRows.length} rows (${filteredItemDetails.length} items grouped)`
                : `Showing ${filteredItemDetails.length} items`}
            </div>

            <div className="overflow-auto" style={{ height: "500px" }}>
              <Table style={{ minWidth: "1200px" }}>
                <THead>
                  <TR>
                    <TH first style={{ minWidth: "300px" }}>
                      {viewMode === "group" ? "Item / Size Group" : "Item"}
                    </TH>
                    <TH style={{ minWidth: "120px" }}>Rate</TH>
                    <TH style={{ minWidth: "100px" }}>Dis 1</TH>
                    <TH style={{ minWidth: "100px" }}>Dis 2</TH>
                    <TH style={{ minWidth: "100px" }}>Dis 3</TH>
                    <TH style={{ minWidth: "100px" }}>Dis 4</TH>
                    <TH style={{ minWidth: "100px" }}>Dis 5</TH>
                    <TH last style={{ minWidth: "120px" }}>
                      {rateColumnLabel}
                    </TH>
                  </TR>
                </THead>
                <TBody>
                  {isItemDetailsLoading ? (
                    <LoadingRow colSpan={8} />
                  ) : viewMode === "flat" ? (
                    filteredItemDetails.length > 0 ? (
                      filteredItemDetails.map((agitem) => {
                        const rateItem = rateList?.find(
                          (item) => item.itemdid === agitem.agitemdtlId
                        );
                        return (
                          <PricelistTableRow
                            key={agitem.agitemdtlId || agitem.itemId}
                            agitem={agitem}
                            rateItem={rateItem}
                            onItemValueChange={setItemValues}
                            rateFieldName={rateFieldName}
                          />
                        );
                      })
                    ) : (
                      <EmptyRow colSpan={8} message="No items found" />
                    )
                  ) : groupedRows.length > 0 ? (
                    groupedRows.map((row) =>
                      row.type === "group" ? (
                        <PricelistGroupRow
                          key={row.groupKey}
                          groupLabel={row.groupKey}
                          sizes={row.sizes}
                          szuom={row.szuom}
                          itemLabel={row.itemLabel}
                          rateItem={row.rateItem}
                          onGroupValueChange={setGroupValues}
                          rateFieldName={rateFieldName}
                        />
                      ) : (
                        <PricelistTableRow
                          key={row.agitem.agitemdtlId}
                          agitem={row.agitem}
                          rateItem={row.rateItem}
                          onItemValueChange={setItemValues}
                          rateFieldName={rateFieldName}
                        />
                      )
                    )
                  ) : (
                    <EmptyRow colSpan={8} message="No items found" />
                  )}
                </TBody>
              </Table>
            </div>
          </>
        )}

        {rateList && (
          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={async () => {
                try {
                  const data = await mutateCreate(rateList);
                  // Reset input changes after successful save
                  setInputChangeRupee(0);
                  setInputChangePercent(0);
                  
                  // Optional: Show success message with stats if available
                  if (data?.stats) {
                    console.log(`Successfully processed: ${data.stats.created} created, ${data.stats.updated} updated`);
                  }
                } catch (error) {
                  console.error("Save failed:", error);
                }
              }}
              disabled={isSavingPricelistDetail}
              className="rounded-md bg-gray-700 px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSavingPricelistDetail ? "Saving..." : "Save"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
